/**
 * lib/teacherLink.ts
 * ------------------------------------------------------------------
 * THE S1 FIX. Resolves which teacher teaches a given subject in a
 * given class — the linkage the whole report/comment/marks system
 * silently depends on and that was previously impossible to create.
 *
 * Faithful to Al-Nur's model (components__StudentsSetup.jsx:
 * teacherTeachesSubjectInClass / getTeachersForSubjectInClass /
 * autoAssignTeacher), but consolidated into one tested helper used
 * by both StudentsSetup (to derive student.subjectTeachers) and any
 * other consumer, so producer and consumer never drift.
 *
 * Authoritative link: teacher.classAssignments[subjectId] -> [classIds].
 * Fallback: teacher.teachingAssignments[formLevel] -> [subjectIds]
 * (a teacher assigned a subject at a form level, with no specific
 *  class list, is assumed to teach all classes at that form).
 * ------------------------------------------------------------------
 */
import type { Teacher } from '@/types';
import { getFormLevel } from '@/lib/grading';

/** Does this teacher teach `subjectId` to this class? */
export function teacherTeachesSubjectInClass(
  teacher: Teacher,
  subjectId: string,
  classId: string,
  className: string,
): boolean {
  // 1. Precise class-level assignment wins (match by classId).
  const classList = teacher.classAssignments?.[subjectId];
  if (classList && classList.length > 0) {
    return classList.includes(classId);
  }
  // 2. Fallback: form-level assignment => teaches all classes at
  //    that form for that subject. Form level is derived from the
  //    class NAME (e.g. "Form 5A" -> "5", "AS" -> "AS").
  const form = getFormLevel(className);
  if (form === null) return false;
  const formSubjects = teacher.teachingAssignments?.[form] ?? [];
  return formSubjects.includes(subjectId);
}

/** All teachers who teach `subjectId` to this class. */
export function getTeachersForSubjectInClass(
  teachers: Teacher[],
  subjectId: string,
  classId: string,
  className: string,
): Teacher[] {
  return teachers.filter((t) =>
    teacherTeachesSubjectInClass(t, subjectId, classId, className),
  );
}

/**
 * Auto-assign: if exactly one teacher teaches this subject in this
 * class, return their id; otherwise null (caller must let a human
 * choose). Mirrors Al-Nur's autoAssignTeacher.
 */
export function autoAssignTeacher(
  teachers: Teacher[],
  subjectId: string,
  classId: string,
  className: string,
): string | null {
  const matches = getTeachersForSubjectInClass(
    teachers,
    subjectId,
    classId,
    className,
  );
  return matches.length === 1 ? matches[0].id : null;
}

/**
 * Resolve the full subjectTeachers map for a student given their
 * class + enrolled subjects. Keeps any explicit existing choices,
 * auto-fills the rest where unambiguous. Returns the new map and
 * the list of subjectIds still needing a manual choice (ambiguous
 * or unassigned) so the UI can surface them.
 */
export function resolveStudentSubjectTeachers(
  teachers: Teacher[],
  classId: string,
  className: string,
  subjectIds: string[],
  existing: Record<string, string> = {},
): { map: Record<string, string>; needsChoice: string[] } {
  const map: Record<string, string> = {};
  const needsChoice: string[] = [];
  for (const sid of subjectIds) {
    if (existing[sid]) {
      const stillValid = getTeachersForSubjectInClass(
        teachers,
        sid,
        classId,
        className,
      ).some((t) => t.id === existing[sid]);
      if (stillValid) {
        map[sid] = existing[sid];
        continue;
      }
    }
    const auto = autoAssignTeacher(teachers, sid, classId, className);
    if (auto) map[sid] = auto;
    else needsChoice.push(sid);
  }
  return { map, needsChoice };
}
